import type { User } from "firebase/auth";

type BeginGmailOAuthParams = {
  /** The signed-in user: the backend reads who they are from their token */
  user: User;
  successRedirect?: string;
  errorRedirect?: string;
  apiBase?: string;
};

const DEFAULT_API_BASE =
  process.env.REACT_APP_API_URL ?? "http://localhost:5050";

/**
 * Starts the Gmail OAuth handshake by asking the backend for the consent URL
 * and redirecting the browser to it.
 */
export async function beginGmailOAuth({
  user,
  successRedirect,
  errorRedirect,
  apiBase = DEFAULT_API_BASE,
}: BeginGmailOAuthParams): Promise<void> {
  const url = new URL(`${apiBase}/connect-gmail`);
  if (user.displayName) {
    url.searchParams.set("user_name", user.displayName);
  }
  url.searchParams.set(
    "success_redirect",
    successRedirect ?? `${window.location.origin}/onboarding`
  );
  url.searchParams.set(
    "error_redirect",
    errorRedirect ?? `${window.location.origin}/`
  );

  const response = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${await user.getIdToken()}` },
  });
  if (!response.ok) {
    throw new Error("Failed to initiate Gmail connection");
  }

  const data = await response.json();
  if (data?.error === "AUTH_REQUIRED") {
    window.location.href = "/login?reason=gmail_auth_required";
    return;
  }

  const authUrl = data?.auth_url;
  if (typeof authUrl !== "string") {
    throw new Error("Invalid Gmail authorization response");
  }

  window.location.assign(authUrl);
}
